import React, { useEffect } from 'react';
import { useState } from "react";
import PopupBase from "../base/PopupBase";
import CustomButton from "../../components/buttons/CustomButton";
import { TextField, Select, MenuItem, FormControl, InputLabel, FormControlLabel, Switch, Dialog } from "@mui/material";
import IconMoney from '@mui/icons-material/MonetizationOnOutlined';
import IconDelete from '@mui/icons-material/DeleteForeverOutlined';
import IconNegative from '@mui/icons-material/Cancel';
import IconPositive from '@mui/icons-material/Done';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import ConfirmPopup from "../ConfirmPopup/ConfirmPopup";
import { DataProviderInstance } from "../../api/DataProvider";
import { GetFriendlyCPF } from '../../utils/Utils';
import PackagePurchaseFlow from "../../components/package-purchase/PackagePurchaseFlow";
import FiscalInvoicePopup from "../FiscalInvoicePopup/FiscalInvoicePopup";
import { formatMessagePrice } from "../../utils/appointmentWhatsAppUtils";
import { parseMoney, formatMoneyInput, cleanMoneyTyping } from "../../utils/moneyUtils";
import { createInvoiceWithLines } from "../../api/AppointmentBillingAPI";
import InvoiceLines from "../../pages/appointments/v2/components/AppointmentDetailPanel/InvoiceLines";
import useBillingLines from "../../pages/appointments/v2/components/AppointmentDetailPanel/useBillingLines";
import useSubscriptionFeatures from "../../hooks/useSubscriptionFeatures";
import { SUBSCRIPTION_PERMISSIONS } from "../../consts/SubscriptionFeatures";

/**
 * Faturar / editar o faturamento de um agendamento (página do atendimento e
 * fluxo com NFS-e da agenda). Ao criar, uma linha por procedimento (InvoiceLines):
 * pacote desconta uma sessão, avulso soma no valor; tudo numa chamada só
 * (createInvoiceWithLines). Só de pacote grava paymentMethod 'credits' e valor 0.
 */
function CreateInvoicePopup(props) {

    const getModifier = (id) => {
        // Se estiver usando créditos, não há modificador de preço
        if (id == 'credits') return 0;
        return CompanyConfig.PaymentOptions.find(item => item.Id == id)?.PriceModifier || 0;
    }

    const getPrice = () => {
        // Ao criar, o valor base é a soma dos avulsos; ao editar, o valor do agendamento
        const base = editing ? Number(Data.price) : directTotal;
        if (paymentMethod == 'credits') return base;
        let modifier = getModifier(paymentMethod);
        return base + Number(modifier);
    }

    const { Data, OnHide, OnCreated } = props;

    const { CompanyConfig, UserData, session, UpdateInvoice, GetPatientById, GetProcedureById, SetPatientDocument, GetInvoice, GetPatientWallet, DeleteInvoice } = DataProviderInstance();

    const [editing, setEditing] = useState(Data.invoiceId != "0");

    // Linhas da fatura (só ao criar). Plano sem pacotes: tudo avulso, opção com cadeado
    const { hasPermission } = useSubscriptionFeatures();
    const packagesLocked = !hasPermission(SUBSCRIPTION_PERMISSIONS.SERVICE_PACKAGES);
    const billing = useBillingLines(Data, { enabled: !editing, locked: packagesLocked });
    const { directTotal, allPackage } = billing.summary;

    const [paymentMethod, setPaymentMethod] = useState(CompanyConfig.PaymentOptions[0]?.Id);
    // Texto do campo no formato brasileiro ("1.200,99"); vira número só ao gravar
    const [currentPrice, setPrice] = useState(formatMoneyInput(editing ? Number(Data.price) : 0));
    const parsedPrice = parseMoney(currentPrice);
    const invalidPrice = parsedPrice === null;
    const [issueDocument, setIssueDocument] = useState(0);

    const [invoiceData, setInvoiceData] = useState(null);
    const [walletData, setWalletData] = useState(null);
    const [isPackagePurchaseOpen, setIsPackagePurchaseOpen] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [showFiscalInvoicePopup, setShowFiscalInvoicePopup] = useState(false);
    const [creating, setCreating] = useState(false);
    const [lineError, setLineError] = useState('');

    // Carteira só para a venda de pacote quando as linhas não trouxerem o wallet_id
    useEffect(() => {
        if (Data.patient && !editing) {
            GetPatientWallet(Data.patient, (response) => {
                if (response.success) setWalletData(response.data);
            });
        }
    }, [Data.patient]);

    useEffect(() => {
        setPrice(formatMoneyInput(getPrice()));
    }, [paymentMethod, directTotal]);

    useEffect(() => {
        if(editing)
        {
            GetInvoice(Data.invoiceId, (data) => {
                // Sem pagamento o endpoint responde 200 com invoice nulo
                setInvoiceData(data?.invoice ? data : null);
            });
        }

    }, [editing]);

    useEffect(() => {
        if(invoiceData != null)
        {
            setPrice(formatMoneyInput(invoiceData.invoice.price));
            setPaymentMethod(invoiceData.invoice.paymentMethod);
            setIssueDocument(invoiceData.invoice.issueDocument == "1" ? 1 : 0);
        }
    }, [invoiceData]);

    function OnCloseClickHandler() {
        Hide(false);
    }

    function Hide(needsPageReload) {
        OnHide(needsPageReload);
    }

    // Fatura em uma chamada: pacotes descontados e avulsos cobrados na mesma transação
    function createInvoiceHandler() {
        if (creating || billing.loading) return;
        if (!allPackage && (invalidPrice || parsedPrice < 0)) return;
        setLineError('');
        setCreating(true);

        createInvoiceWithLines({
            appointmentId: Data.id,
            companyId: session?.currentCompany ?? CompanyConfig?.Id ?? '',
            createdBy: UserData?.id,
            patient: Data.patient,
            price: allPackage ? 0 : parsedPrice,
            paymentMethod: allPackage ? 'credits' : paymentMethod,
            procedure: Data.procedure,
            issueDocument: allPackage ? 0 : issueDocument,
            lines: billing.payloadLines,
        }, (response) => {
            setCreating(false);
            if (response?.status === 'SUCCESS') {
                if(OnCreated) OnCreated(response);
                Hide(true);
                return;
            }
            // Mensagem do backend no rodapé das linhas; pacote sem saldo (409
            // NO_CREDITS) recarrega as linhas com o saldo real
            setLineError(response?.message || response?.error || 'Erro ao criar faturamento.');
            if (response?.httpStatus === 409 || response?.code === 'NO_CREDITS') billing.reload();
        });
    }
    
    function updateInvoiceHandler() {

        if (invalidPrice) return;
        const invoiceId = invoiceData.invoice.id;

        const price = parsedPrice;
        const pay = paymentMethod;
        const issue = issueDocument;

        UpdateInvoice(invoiceId, price, pay, issue, (response) => {
            Hide(true);
        });
    }

    function deleteInvoiceHandler() {
        if (invoiceData && invoiceData.invoice && invoiceData.invoice.id) {
            
            setShowDeleteConfirmation(true);
        } else {
            
        }
    }

    function confirmDeleteInvoice() {
        
        
        DeleteInvoice(invoiceData.invoice.id, (response) => {
            
            if (response.status == "SUCCESS") {
                // Notify parent component about the deletion to refresh appointments
                if(OnCreated) OnCreated(response);
                Hide(true); // Close popup and refresh page
            } else {
                alert("Erro ao excluir faturamento: " + response.message);
            }
        });
        setShowDeleteConfirmation(false);
    }

    function hideDeleteConfirmation() {
        
        setShowDeleteConfirmation(false);
    }

    function createInvoiceDocument(callback) {
        const appointmentData = Data;
        const patientData = GetPatientById(appointmentData.patient);
        const procedureData = GetProcedureById(appointmentData.procedure);
        const appointmentDate = new Date(appointmentData.date);

        const documentTemplate = CompanyConfig.DocumentTemplates.find(item => item.tag == "Recibo");

        const currentTitle = documentTemplate.title;

        const friendyDate = appointmentDate.toLocaleDateString('pt-BR', { year: 'numeric', month: 'long', day: 'numeric' });

        const cpf = GetFriendlyCPF(patientData.cpf);
        const price = parsedPrice ?? 0;

        const paymentMethodLabel = paymentMethod == 'credits' ? 'Créditos do Pacote' : CompanyConfig.PaymentOptions.find(item => item.Id == paymentMethod).Label;

        const currentText = documentTemplate.text
            .replace("{patient-name}", "" + patientData.name + "")
            .replace("{patient-cpf}", "" + cpf + "")
            .replace("{appointment-date}", "" + friendyDate + "")
            .replace("{appointment-price}", formatMessagePrice(price) + " (" + paymentMethodLabel + ")");

        const formattedAppointmentDate = appointmentDate.toLocaleDateString('pt-BR', { year: 'numeric', month: '2-digit', day: '2-digit' }).split('/').reverse().join('-');

        SetPatientDocument(appointmentData.patient, formattedAppointmentDate, appointmentData.id, currentTitle, currentText, (response) => {
            callback(response);
        });
    }

    function handlePaymentMethodChange(event) {
        setPaymentMethod(event.target.value);
    }

    function GetPositiveButton() {
        // Se estiver editando e for pagamento com créditos, não mostra botão de salvar
        if(editing && (paymentMethod == 'credits' || paymentMethod == "0")) {
            return null;
        }
        
        if(editing) {
            return (
                <CustomButton
                    variant="contained"
                    style="primary"
                    label="Salvar"
                    enabled={!invalidPrice}
                    onClick={updateInvoiceHandler}
                />
            );
        }

        const canCreate = (allPackage || (!invalidPrice && parsedPrice >= 0)) && !billing.loading && !creating;
        return <CustomButton variant="contained" style="primary" label={creating ? "Faturando..." : "Criar fatura"} icon={<IconMoney />} enabled={canCreate} onClick={createInvoiceHandler} data-tour="payment-save" />
    }

    function getModifierLabel(id) {
        // Se estiver usando créditos, não mostra modificador
        if (id == 'credits') return "";
        
        const option = CompanyConfig.PaymentOptions.find(item => item.Id == id);
        if (!option) return "";
        
        const modifier = option.PriceModifier;
        if (modifier == 0) return "";
        
        return modifier > 0
            ? `(+ R$${modifier})`
            : `(- R$${modifier * -1})`;
    }

    function GetContent() {

        let friendlyDate = new Date().toLocaleDateString("pt-PT", { year: 'numeric', month: 'numeric', day: 'numeric' });

        // Vários procedimentos: "Axila + Virilha + Pernas"
        const procedureLabel = billing.lines.length > 0
            ? billing.lines.map(line => line.procedure_name).join(' + ')
            : (GetProcedureById(Data.procedure)?.Label || '');

        // Ao editar vale o método salvo; ao criar, "só pacote" dispensa a cobrança
        const usingCredits = editing
            ? (paymentMethod == 'credits' || paymentMethod == "0")
            : allPackage;

        return (
            <div style={{ marginTop: "10px" }}>
                {/* Botão Emitir NFS-e + Excluir - Only shown when editing */}
                {editing && (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '20px',
                        gap: '10px',
                        flexWrap: 'wrap',
                    }}>
                        <CustomButton
                            variant="contained"
                            style="secondary"
                            label="Nota Fiscal"
                            icon={<ReceiptLongOutlinedIcon />}
                            onClick={() => setShowFiscalInvoicePopup(true)}
                        />
                        <CustomButton
                            variant="contained"
                            style="error"
                            label="Excluir faturamento"
                            icon={<IconDelete />}
                            onClick={deleteInvoiceHandler}
                            customStyle={{
                                backgroundColor: '#d32f2f',
                                color: 'white',
                                fontWeight: 'bold',
                                padding: '10px 20px',
                                boxShadow: '0 4px 8px rgba(211, 47, 47, 0.3)'
                            }}
                        />
                    </div>
                )}
                <div className="form">
                    <div className="form-group">
                        <div className="form-line">
                            <TextField label="Procedimento" value={procedureLabel} disabled />
                            <TextField label="Data" value={friendlyDate} disabled />
                        </div>
                    </div>
                    <TextField label="Paciente" value={GetPatientById(Data.patient).name} disabled />
                    
                    {/* Indicação de pagamento via créditos quando estiver editando */}
                    {editing && (paymentMethod == 'credits' || paymentMethod == "0") && (
                        <div className="form-group" style={{ marginTop: '20px', padding: '15px', backgroundColor: '#e3f2fd', borderRadius: '4px' }}>
                            <div style={{ color: '#1976d2'}}>
                                Este atendimento foi pago utilizando créditos do pacote
                            </div>
                        </div>
                    )}
                    
                    {/* Linhas da fatura (pacote x avulso por procedimento) - só ao criar.
                        "Vender pacote" abre o PackagePurchaseFlow, que no plano sem pacotes
                        mostra o aviso de upgrade. */}
                    {!editing && (
                        <div className="form-group" style={{ marginTop: '20px' }}>
                            <InvoiceLines
                                lines={billing.lines}
                                choices={billing.choices}
                                onChange={(procedureId, source) => { billing.setChoice(procedureId, source); setLineError(''); }}
                                locked={packagesLocked}
                                lockAction={
                                    <button type="button" onClick={() => setIsPackagePurchaseOpen(true)}>Ver planos</button>
                                }
                                onLockedClick={() => setIsPackagePurchaseOpen(true)}
                                onSellPackage={() => setIsPackagePurchaseOpen(true)}
                                loading={billing.loading}
                                disabled={creating}
                                error={lineError}
                            />
                        </div>
                    )}

                    {/* Seção de Pagamento - só aparece se não estiver usando créditos */}
                    {!usingCredits && (
                        <div className="form-group">
                            <div className="form-line">
                                <TextField
                                    label="Valor (R$)"
                                    value={currentPrice}
                                    inputProps={{ inputMode: 'decimal' }}
                                    placeholder="0,00"
                                    onChange={(e) => { setPrice(cleanMoneyTyping(e.target.value)) }}
                                    onBlur={() => { if (!invalidPrice) setPrice(formatMoneyInput(parsedPrice)) }}
                                    error={invalidPrice}
                                    helperText={invalidPrice ? 'Use o formato 150,00 ou 1.200,99' : undefined}
                                    disabled={paymentMethod == 'credits'}
                                    data-tour={editing ? undefined : 'payment-price'}
                                />
                                {(paymentMethod == 'credits' || paymentMethod == "0") ? (
                                    <TextField
                                        label="Método de pagamento"
                                        value="Utilização de créditos"
                                        disabled
                                        style={{ flex: "1" }}
                                    />
                                ) : (
                                    <FormControl style={{ flex: "1" }} data-tour={editing ? undefined : 'payment-method'}>
                                        <InputLabel>Método de pagamento</InputLabel>
                                        <Select
                                            value={paymentMethod}
                                            label="Método de pagamento"
                                            onChange={handlePaymentMethodChange}>
                                            {CompanyConfig.PaymentOptions.map((option) => (
                                                <MenuItem key={option.Id} value={option.Id}>
                                                    {option.Label} {getModifierLabel(option.Id)}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            </div>
                            <div className="form-line">
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={issueDocument}
                                            onChange={(e) => { setIssueDocument(e.target.checked ? 1 : 0) }}
                                        />
                                    }
                                    label="Emitir nota fiscal ?"
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        )
    }

    const GetTitle = () => {
        if(editing)
        {
            return "Editar faturamento" ;
        }
        
        return "Faturar atendimento";
    }

    // Pacote vendido: as linhas voltam com o saldo novo (e já nascem como "Pacote")
    const handlePurchaseComplete = () => {
        billing.reload();
    };

    return (
        <>
            <PopupBase Title={GetTitle()} Content={GetContent()} Footer={GetPositiveButton()} OnClose={OnCloseClickHandler} />
            
            <PackagePurchaseFlow
                open={isPackagePurchaseOpen}
                onClose={() => setIsPackagePurchaseOpen(false)}
                onPurchaseComplete={handlePurchaseComplete}
                companyConfig={CompanyConfig}
                walletId={billing.walletId ?? walletData?.wallet_id}
            />

            {/* FiscalInvoicePopup - abre sobre o CreateInvoicePopup */}
            {showFiscalInvoicePopup && (
                <FiscalInvoicePopup
                    appointmentData={{
                        id: Data.id,
                        price: parsedPrice ?? 0,
                        invoiceId: Data.invoiceId,
                        date: Data.date,
                        procedure: Data.procedure,
                        resource: Data.resource,
                    }}
                    patientData={GetPatientById(Data.patient)}
                    onHide={() => setShowFiscalInvoicePopup(false)}
                />
            )}

            {/* Delete Confirmation Dialog - Renderizado no nível raiz para evitar problemas de aninhamento */}
            {showDeleteConfirmation && (
                <Dialog
                    open={true}
                    onClose={hideDeleteConfirmation}
                    maxWidth="sm"
                    fullWidth
                    style={{ zIndex: 2100 }} // zIndex maior que o Dialog principal
                    PaperProps={{
                        style: {
                            borderRadius: 'var(--border-radius)',
                            padding: '25px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '15px',
                            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
                        }
                    }}
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h2 style={{ fontWeight: 'bold', margin: 0, color: '#444' }}>Excluir faturamento</h2>
                        <CloseOutlinedIcon
                            style={{ cursor: 'pointer' }}
                            onClick={hideDeleteConfirmation}
                            fontSize="large"
                        />
                    </div>
                    
                    <div style={{ overflow: 'auto' }}>
                        <div>
                            <strong>Tem certeza que deseja excluir este faturamento?</strong><br />
                            Esta ação não pode ser desfeita.
                        </div>
                        
                        {(paymentMethod == 'credits' || paymentMethod == "0") && (
                            <div style={{
                                backgroundColor: '#e3f2fd',
                                padding: '15px',
                                borderRadius: '4px',
                                marginTop: '15px',
                                borderLeft: '4px solid #2196F3'
                            }}>
                                <div style={{ color: '#0d47a1', fontWeight: 'bold', margin: 0 }}>
                                    Ao excluir este faturamento, o crédito utilizado será restaurado para o paciente.
                                </div>
                            </div>
                        )}
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
                        <CustomButton
                            variant="outlined"
                            style="secondary"
                            label="Cancelar"
                            icon={<IconNegative />}
                            onClick={hideDeleteConfirmation}
                        />
                        <CustomButton
                            variant="contained"
                            style="error"
                            label="Excluir faturamento"
                            icon={<IconPositive />}
                            onClick={confirmDeleteInvoice}
                        />
                    </div>
                </Dialog>
            )}
        </>
    );
}

export default CreateInvoicePopup;